import common from './common';
import Logger from './utils/Logger';

export default class SaveData {
	constructor() {
		this._game = {
			language: null,
			sound: true,
			motion: true,
		};

		this._editor = {
			camera: {
				x: 100,
				y: 100,
				zoom: 1,
			},
			settings: {
				ground: 100,
				structure: 2,
				risk: 0,
				level: 0,
			},
			map: {
				columns: 20,
				rows: 40,
				tileset: 'tileset_ts_data',
				structset: 'structset_ts_data',
				upgradeset: 'upgradeset_ts_data',
				data: {
					ground: [],
					structure: [],
					level: [],
					risk: [],
					disabled: [],
				},
			},
		};
	}

	_findUrlLanguage() {
		// If a specific language has been asked for in the URL, use it
		const urlParams = new URLSearchParams(window.location.search);
		const urlLang = urlParams.get('lang');

		if (Object.keys(common.i18nConfig.languages).find((lang) => lang === urlLang)) {
			Logger.log('urlLanguage', urlLang);

			// Track language change from deep link before redirecting
			// Note: scenario and difficulty are already tracked via deep-link handling
			// (select_level event in Application), so no need to duplicate here.
			try {
				common.tracking && common.tracking.track(new P3.TrackingDataGtagEvent('change_language', {
					event_category: 'UI',
					event_type: `change_lang_${urlLang}`,
					page_container: 'game',
				}));
			} catch (e) { /* no-op */ }

			// Strip the language from the URL to prevent recursion in language selection
			const currentUrl = window.location.href;
			const strippedUrl = currentUrl.replace(/(\?|&)lang=[^&]*(&|$)/, '$1');

			window.location.href = strippedUrl.replace(/[?&]$/, '');

			return urlLang;
		}

		return false;
	}

	_findNavigatorLanguage() {
		// If no language is requested, look to the browser for the language
		const navigatorLanguage = navigator.language;

		Logger.log('navigatorLanguage', navigatorLanguage);

		if (!navigatorLanguage) {
			return 'en';
		}

		let langCode = Object.keys(common.i18nConfig.languages).find((lang) => lang === navigatorLanguage);

		if (!langCode) {
			// try again with the first part of the language as a close match
			const parts = navigatorLanguage.split('-');
			const firstPart = parts[0];

			langCode = Object.keys(common.i18nConfig.languages).find((lang) => lang === firstPart);
		}

		if (!langCode) {
			langCode = 'en';
		}

		return langCode;
	}

	/**
	 * Get the language of the game. Priority is URL, cookie, then navigator language, then default to English.
	 */
	getLanguage() {
		// A URL asked for language always takes precedence
		const urlLang = this._findUrlLanguage();

		if (urlLang) {
			this._game.language = urlLang;

			return this._game.language;
		}

		// If no language is requested, look to the browser for the language
		if (!this._game.language) {
			this._game.language = this._findNavigatorLanguage();
		}

		return this._game.language;
	}

	loadGame() {
		const key = common.config.save.game.name + common.config.version;
		let data = localStorage.getItem(key);

		if (!data) {
			data = this.saveGame();
		}

		this._game = JSON.parse(data);
		common.config.language = this.getLanguage();
		common.motion = this._game.motion;
		P3.audio.mute(!this._game.sound);

		// In cases where the language or other settings are switched, ensure the changes are saved before reloading
		this.saveGame();

		// todo load the game
	}

	saveGame() {
		const key = common.config.save.game.name + common.config.version;
		const data = P3.utils.copyObject(this._game, true);

		data.hash = P3.utils.md5(`${JSON.stringify(data)}dd8036ae6012266cf575c5aacb8443ad`);

		const payload = JSON.stringify(data);

		localStorage.setItem(key, payload);

		return payload;
	}

	loadEditor() {
		const key = common.config.save.editor.name + common.config.version;
		let data = localStorage.getItem(key);

		if (!data) {
			this.saveEditor();

			return;
		}
		data = JSON.parse(data);
		this._editor = data;
	}

	saveEditor() {
		const key = common.config.save.editor.name + common.config.version;
		const data = P3.utils.copyObject(this._editor, true);

		data.hash = P3.utils.md5(`${JSON.stringify(data)}dd8036ae6012266cf575c5aacb8443ad`);

		const payload = JSON.stringify(data);

		localStorage.setItem(key, payload);

		return payload;
	}

	get game() {
		return this._game;
	}

	get editor() {
		return this._editor;
	}
}
